import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './DrawerPortal.module.css';

interface DrawerPortalProps {
  children: React.ReactNode;
}

/**
 * DrawerPortal component that renders its children at the root level of the DOM
 * This prevents z-index stacking context issues
 */
const DrawerPortal: React.FC<DrawerPortalProps> = ({ children }) => {
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    // Check if the portal container already exists
    let container = document.getElementById('drawer-portal-container');
    
    // If not, create it
    if (!container) {
      container = document.createElement('div');
      container.id = 'drawer-portal-container';
      container.style.position = 'fixed';
      container.style.top = '0';
      container.style.left = '0';
      container.style.width = '100%';
      container.style.height = '100%';
      container.style.pointerEvents = 'none';
      container.style.zIndex = '9999'; // Very high z-index
      document.body.appendChild(container);
    }
    
    setPortalContainer(container);
    
    // Cleanup function
    return () => {
      // Only remove the container if it has no children and is still a child of document.body
      if (container && container.childNodes.length === 0 && container.parentNode === document.body) {
        document.body.removeChild(container);
      }
    };
  }, []);

  // Don't render until we have a container
  if (!portalContainer) return null;
  
  // Use createPortal to render children into the portal container
  return createPortal(
    <div className={styles.portalContent}>
      {children}
    </div>,
    portalContainer
  );
};

export default DrawerPortal;
